import React from 'react';
import PropTypes from 'prop-types';
import { FormControl } from './FormControl/component.legacy';
import { CheckboxControl } from './CheckboxControl';
import { CheckboxGroup } from 'react-checkbox-group';
import { i18n } from '../../../i18n/translate';
import { kebabCase } from 'lodash';
import { simpleArrayMatch } from '../../../utils/simpleArrayMatch';
import { Stack } from '@chakra-ui/react';

export class CheckboxGroupControl extends FormControl {
  static propTypes = {
    CheckboxComponent: PropTypes.func,
    className: PropTypes.string,
    dataObjectAction: PropTypes.string,
    dataObjectAttribute: PropTypes.string,
    dataObjectId: PropTypes.string,
    dataObjectType: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    inline: PropTypes.bool,
    labelFunction: PropTypes.func,
    labelKey: PropTypes.string.isRequired,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onValueChange: PropTypes.func,
    readOnlyList: PropTypes.array,
    required: PropTypes.bool,
    selectedClassName: PropTypes.string,
    valueList: PropTypes.array.isRequired,
    valueKey: PropTypes.string.isRequired,
    truncateSize: PropTypes.number,
    useCheckboxValue: PropTypes.bool,
  };

  static defaultProps = Object.assign({}, FormControl.defaultProps, {
    CheckboxComponent: CheckboxControl,
  });

  constructor(props) {
    super(props);
    this.state = {
      showMoreValues: !props.truncateSize,
      value: props.dataValue || [],
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.valueOverride && prevProps.dataValue !== this.props.dataValue) {
      this.syncData(this.props.dataValue || [], true);
    }
  }

  checkboxChange = (value, checked) => {
    const newValue = this.state.value.slice(0);
    const valIndex = this.state.value.findIndex(val => val === value);
    if (checked && valIndex < 0) {
      newValue.push(value);
    } else if (!checked && valIndex >= 0) {
      newValue.splice(valIndex, 1);
    }
    this.syncData(newValue);
  };

  syncData = (newValue, skipValueChange) => {
    if (!simpleArrayMatch(newValue || [], this.state.value)) {
      this.setState({ value: newValue }, () => {
        if (typeof this.props.onValueChange === 'function') {
          this.props.onValueChange({ [this.props.name]: newValue });
        }
        if (typeof this.props.onChange === 'function') {
          this.props.onChange(newValue, skipValueChange);
        }

        this.checkError();
      });
    }
  };

  toggleShowMore = () => {
    this.setState({
      showMoreValues: !this.state.showMoreValues,
    });
  };

  isValid = () => {
    return !this.props.required || this.state.value.length;
  };

  checkError() {
    const fieldLabel = this.props.caption || this.props.name;
    const message = !this.isValid()
      ? fieldLabel
        ? i18n.t('%s is required. Please select at least one option', { postProcess: 'sprintf', sprintf: [fieldLabel] })
        : i18n.t('This field is required. Please select at least one option')
      : null;
    if (typeof this.props.setError === 'function') {
      this.props.setError(!this.isValid(), message);
    }
  }

  renderCheckbox = listItem => {
    const { CheckboxComponent, inline, labelFunction, labelKey, readOnlyList, name } = this.props;
    const itemValue = listItem[this.props.valueKey];
    const isSelected = this.state.value.includes(itemValue);

    return (
      <Stack key={itemValue} direction={inline ? 'row' : 'column'} gap="2">
        <CheckboxComponent
          name={`${name}[${itemValue}]`}
          data-object-action={this.props.dataObjectAction || `check-${kebabCase(listItem[labelKey])}`}
          data-object-attribute={this.props.dataObjectAttribute}
          data-object-id={itemValue}
          data-object-type={this.props.dataObjectType}
          valueOverride={this.props.valueOverride}
          checkboxValue={this.props.useCheckboxValue ? itemValue : undefined}
          label={listItem[labelKey]}
          dataValue={isSelected}
          className={isSelected ? this.props.selectedClassName : null}
          onChange={checked => this.checkboxChange(itemValue, checked)}
          disabled={this.props.disabled || (readOnlyList && readOnlyList.includes(itemValue))}
          id={this.props.id}
          inline={this.props.inline}
        >
          {typeof labelFunction === 'function' ? labelFunction(listItem) : null}
        </CheckboxComponent>
      </Stack>
    );
  };

  render() {
    const { valueList = [], name, truncateSize, readOnlyList } = this.props;
    const readOnlySelected = readOnlyList && readOnlyList.filter(readOnly => this.state.value.includes(readOnly));
    const truncatedData = truncateSize ? valueList.slice(0, truncateSize) : valueList;

    return (
      <div aria-describedby={this.props['aria-describedby']} aria-invalid={this.props['aria-invalid']}>
        <CheckboxGroup
          name={name}
          onChange={this.onChange}
          value={this.state.value}
          className={`checkbox-group ${this.props.className || ''}`}
        >
          {this.state.showMoreValues || valueList.length <= truncateSize
            ? valueList.map(this.renderCheckbox)
            : truncatedData.map(this.renderCheckbox)}
          {truncateSize < valueList.length ? (
            <div className="show-more-link">
              <a onClick={this.toggleShowMore}>
                {this.state.showMoreValues ? i18n.t('Show Less') : i18n.t('Show More')}
              </a>
            </div>
          ) : null}
        </CheckboxGroup>
        {/* key seems to mess up tests on these inputs for some reason  */}
        {/* eslint-disable-next-line react/jsx-key */}
        {readOnlySelected && readOnlySelected.map(val => <input type="hidden" name={name} value={val} />)}
      </div>
    );
  }
}
